import { ArrowBack } from '@mui/icons-material';
import { Box, Container, Divider, IconButton, Paper, Typography, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { Breakpoint } from '@mui/system';
import React, { useEffect, useState } from 'react';

type MyContainerProps = {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: Breakpoint | false;
  disableGutters?: boolean;
  disableContentPadding?: boolean;
  enableBack?: boolean;
  onBack?: () => void;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  children?: React.ReactNode;
};

export function MyContainer(props: MyContainerProps) {
  const theme = useTheme();
  const [availableWidth, setAvailableWidth] = useState('calc(100vw - 64px)');
  const {
    title,
    subtitle,
    actions,
    toolbar,
    footer,
    maxWidth = '2xl',
    disableGutters = true,
    disableContentPadding = false,
    enableBack = true,
    contentSx,
    children,
    onBack,
    sx,
  } = props;

  // Calculate available width based on sidebar state
  const calculateAvailableWidth = () => {
    const isMobile = theme.breakpoints.down('md');

    if (isMobile) return '100vw'; // No sidebar on mobile

    // Check if sidebar is collapsed by looking for the collapsed class
    const sidebarElement = document.querySelector('[data-sidebar="desktop"]');
    const isCollapsed = sidebarElement?.classList.contains('w-16') || false;

    return isCollapsed ? 'calc(100vw - 16px)' : 'calc(100vw - 64px)';
  };

  // Update width when sidebar state changes
  useEffect(() => {
    const updateWidth = () => {
      setAvailableWidth(calculateAvailableWidth());
    };

    // Initial calculation
    updateWidth();

    // Listen for sidebar changes (mutation observer for class changes)
    const sidebarElement = document.querySelector('[data-sidebar="desktop"]');
    if (sidebarElement) {
      const observer = new MutationObserver(updateWidth);
      observer.observe(sidebarElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      return () => observer.disconnect();
    }
  }, [theme.breakpoints]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters={disableGutters}
      sx={{ position: 'relative', width: '100%', maxWidth: availableWidth, overflowX: 'hidden' }}
    >
      <Paper
        variant="elevation"
        elevation={0}
        sx={{
          overflow: 'visible',
          overflowX: 'hidden',
          overflowY: 'visible',
          bgcolor: '#f8fafc',
          boxShadow: 'none',
          border: 'none',
          width: '100%',
          maxWidth: availableWidth,
          minWidth: 0,
          ...(disableContentPadding && {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }),
          position: 'relative',
          ...((sx as object) || {}),
        }}
      >
        {(title || actions || toolbar || enableBack) && (
          <Box
            sx={{
              px: { xs: 1.5, sm: 2, md: 3.5, lg: 4, xl: 5 },
              py: {
                xs: toolbar ? 0.75 : 1,
                sm: toolbar ? 0.75 : 1,
                md: toolbar ? 1.25 : 2,
                lg: toolbar ? 1.5 : 2.5,
                xl: toolbar ? 1.5 : 3,
              },
              position: 'sticky',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Container maxWidth={maxWidth as Breakpoint} disableGutters={disableGutters}>
              {(title || actions || enableBack) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.75, sm: 0.75, md: 1.25, lg: 1.5, xl: 2 },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  {enableBack && (
                    <IconButton
                      onClick={handleBack}
                      sx={{ mr: { xs: -0.25, sm: -0.25, md: -1 }, flexShrink: 0 }}
                      aria-label="Go back"
                    >
                      <ArrowBack />
                    </IconButton>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    {title && (
                      <Typography
                        variant="h6"
                        noWrap
                        title={title}
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '0.875rem',
                            md: '1.125rem',
                            lg: '1.25rem',
                            xl: '1.375rem',
                          },
                        }}
                      >
                        {title}
                      </Typography>
                    )}
                    {subtitle && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
                          fontSize: {
                            xs: '0.7rem',
                            sm: '0.7rem',
                            md: '0.8rem',
                            lg: '0.875rem',
                            xl: '0.9375rem',
                          },
                        }}
                      >
                        {subtitle}
                      </Typography>
                    )}
                  </Box>
                  {actions && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 0.25, sm: 0.25, md: 0.75, lg: 1, xl: 1.25 },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        flexShrink: 0,
                      }}
                    >
                      {actions}
                    </Box>
                  )}
                </Box>
              )}

              {toolbar && (
                <Box
                  sx={{
                    mt: { xs: 0.75, sm: 0.75, md: 1.25, lg: 1.5, xl: 2 },
                    ...(title || actions || enableBack ? {} : { mt: 0 }),
                  }}
                >
                  {toolbar}
                </Box>
              )}
            </Container>
          </Box>
        )}

        {(title || actions || toolbar || enableBack) && <Divider />}

        <Container
          maxWidth={maxWidth as Breakpoint}
          disableGutters={disableGutters}
          sx={{ width: '100%', maxWidth: availableWidth }}
        >
          <Box
            sx={{
              bgcolor: '#f8fafc',
              p: disableContentPadding ? 0 : { xs: 1.5, sm: 2, md: 3.5, lg: 4, xl: 5 },
              width: '100%',
              maxWidth: availableWidth,
              minWidth: 0,
              overflowX: 'hidden',
              ...(disableContentPadding && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }),
              ...((contentSx as object) || {}),
            }}
          >
            {children}
          </Box>
        </Container>

        {footer && (
          <>
            <Divider />
            <Container maxWidth={maxWidth as Breakpoint} disableGutters={disableGutters}>
              <Box
                sx={{
                  bgcolor: '#f8fafc',
                  py: { xs: 1.5, sm: 2, md: 3.5, lg: 4, xl: 5 },
                  px: { xs: 1.5, sm: 2, md: 3.5, lg: 4, xl: 5 },
                }}
              >
                {footer}
              </Box>
            </Container>
          </>
        )}
      </Paper>
    </Container>
  );
}

type SectionProps = {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
};

export function MyContainerSection({ title, description, actions, children, sx }: SectionProps) {
  return (
    <Box sx={{ mb: { xs: 1.5, sm: 1.5, md: 2.5, lg: 3, xl: 3.5 }, ...((sx as object) || {}) }}>
      {(title || actions) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 0.5, sm: 0.5, md: 1, lg: 1.25, xl: 1.5 },
            gap: { xs: 0.75, sm: 0.75, md: 1.25, lg: 1.5, xl: 2 },
            flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
          }}
        >
          {title && (
            <Typography
              variant="subtitle1"
              sx={{
                flex: 1,
                fontSize: {
                  xs: '0.8rem',
                  sm: '0.8rem',
                  md: '0.9375rem',
                  lg: '1rem',
                  xl: '1.0625rem',
                },
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
          )}
          {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
        </Box>
      )}
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: { xs: 0.5, sm: 0.5, md: 1, lg: 1.25, xl: 1.5 },
            display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
            fontSize: { xs: '0.7rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '0.9375rem' },
          }}
        >
          {description}
        </Typography>
      )}
      <Box>{children}</Box>
    </Box>
  );
}

export function MyContainerHeader({ children }: { children?: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 0.75, md: 1.25, lg: 1.5, xl: 2 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      {children}
    </Box>
  );
}

export default MyContainer;
